import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  CostCenterDevelopmentRef,
  CostCenterDto,
  CostCenterFilterOption,
  CostCenterFilterSelects,
  CostCenterFormSelects,
  CostCenterListQuery,
  CostCenterListResult,
  CostCenterRequest,
  CostCenterTypeRef,
} from './cost-centers-api.types';
import { mapSettingsError, SettingsApiError } from './settings-api.errors';
import { SETTINGS_API_URL } from './settings-api.tokens';

const REQUEST_TIMEOUT_MS = 15_000;
const LIST_PAGE_SIZE = 100;
const COST_CENTERS_PATH = '/catalogs/cost-centers/cost-centers';

@Injectable({ providedIn: 'root' })
export class CostCentersApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(SETTINGS_API_URL).replace(/\/+$/, '')}${COST_CENTERS_PATH}`;

  list(query: CostCenterListQuery = {}): Observable<CostCenterListResult> {
    return this.http
      .get<unknown>(this.baseUrl, { params: this.listParams(query) })
      .pipe(
        timeout(REQUEST_TIMEOUT_MS),
        retry({ count: 1, delay: error => this.retryDelay(error) }),
        map(body => unwrapList(body)),
        catchError(error => throwError(() => mapSettingsError(error))),
      );
  }

  formSelects(): Promise<CostCenterFormSelects> {
    return this.request(
      this.http.get<unknown>(`${this.baseUrl}/form-selects`),
      body => unwrapFormSelects(body),
    );
  }

  filterSelects(): Promise<CostCenterFilterSelects> {
    return this.request(
      this.http.get<unknown>(`${this.baseUrl}/filter-selects`),
      body => unwrapFilterSelects(body),
    );
  }

  getById(uuid: string): Promise<CostCenterDto> {
    return this.request(
      this.http.get<unknown>(`${this.baseUrl}/${uuid}`),
      body => unwrapItem(body),
    );
  }

  create(payload: CostCenterRequest): Promise<CostCenterDto> {
    return this.request(
      this.http.post<unknown>(this.baseUrl, payload),
      body => unwrapItem(body),
    );
  }

  update(uuid: string, payload: CostCenterRequest): Promise<CostCenterDto> {
    return this.request(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      body => unwrapItem(body),
    );
  }

  activate(uuid: string): Promise<void> {
    return this.request(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/activate`, null),
      () => undefined,
    );
  }

  inactivate(uuid: string): Promise<void> {
    return this.request(
      this.http.patch<unknown>(`${this.baseUrl}/${uuid}/inactivate`, null),
      () => undefined,
    );
  }

  remove(uuid: string): Promise<void> {
    return this.request(
      this.http.delete<unknown>(`${this.baseUrl}/${uuid}`),
      () => undefined,
    );
  }

  private async request<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapSettingsError(error);
    }
  }

  private listParams(query: CostCenterListQuery): HttpParams {
    let params = new HttpParams()
      .set('page', String(query.page ?? 0))
      .set('size', String(query.size ?? LIST_PAGE_SIZE));

    if (query.code) params = params.set('filters[code]', query.code);
    if (query.name) params = params.set('filters[name]', query.name);
    if (query.description) params = params.set('filters[description]', query.description);
    if (query.development) params = params.set('filters[development]', query.development);
    if (query.typeUuids?.length) {
      params = params.set('select[type]', query.typeUuids.join(','));
    }
    if (query.status?.length) {
      params = params.set('select[status]', query.status.join(','));
    }
    if (query.sortBy) params = params.set('sortBy', query.sortBy);
    if (query.order) params = params.set('order', query.order);

    return params;
  }

  private retryDelay(error: unknown): Observable<number> {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 429) {
        const retryAfter = Number(error.headers.get('Retry-After'));
        const seconds =
          Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
        return timer(seconds * 1000);
      }
      if (error.status >= 500 || error.status === 0) {
        return timer(300);
      }
    }
    return throwError(() => error);
  }
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function unwrapList(body: unknown): CostCenterListResult {
  const data = unwrapData(body);
  if (Array.isArray(data)) {
    return { items: data.map(normalizeListItem), total: data.length };
  }

  if (data && typeof data === 'object') {
    const page = data as {
      data?: unknown;
      content?: unknown;
      items?: unknown;
      pagination?: { totalRecords?: unknown };
      totalElements?: unknown;
      total?: unknown;
    };
    const nestedItems = page.data;
    if (Array.isArray(nestedItems)) {
      const totalRecords = page.pagination?.totalRecords;
      return {
        items: nestedItems.map(normalizeListItem),
        total: typeof totalRecords === 'number' ? totalRecords : nestedItems.length,
      };
    }
    if (Array.isArray(page.content)) {
      const items = page.content.map(normalizeListItem);
      return {
        items,
        total:
          typeof page.totalElements === 'number' ? page.totalElements : items.length,
      };
    }
    if (Array.isArray(page.items)) {
      const items = page.items.map(normalizeListItem);
      return {
        items,
        total: typeof page.total === 'number' ? page.total : items.length,
      };
    }
  }

  throw new SettingsApiError('unexpected', 0);
}

function normalizeListItem(raw: unknown): CostCenterDto {
  const record = asRecord(raw);
  if (!record) throw new SettingsApiError('unexpected', 0);
  return normalizeItem(record);
}

function unwrapItem(body: unknown): CostCenterDto {
  const data = unwrapData(body);
  if (!data || typeof data !== 'object') {
    throw new SettingsApiError('unexpected', 0);
  }

  const item = normalizeItem(data as Record<string, unknown>);
  if (!item.uuid || typeof item.code !== 'string' || typeof item.name !== 'string') {
    throw new SettingsApiError('unexpected', 0);
  }
  return item;
}

function normalizeItem(raw: Record<string, unknown>): CostCenterDto {
  const type = normalizeTypeRef(raw['type']);
  const development = normalizeDevelopmentRef(raw['development']);
  return {
    uuid: String(raw['uuid'] ?? ''),
    code: String(raw['code'] ?? ''),
    name: String(raw['name'] ?? ''),
    description: typeof raw['description'] === 'string' ? raw['description'] : null,
    type,
    development,
    typeUuid: typeof raw['typeUuid'] === 'string' ? raw['typeUuid'] : type?.uuid,
    developmentUuid:
      typeof raw['developmentUuid'] === 'string'
        ? raw['developmentUuid']
        : (development?.uuid ?? null),
    status: raw['status'] as CostCenterDto['status'],
    enabled: typeof raw['enabled'] === 'boolean' ? raw['enabled'] : undefined,
    createdAt: typeof raw['createdAt'] === 'string' ? raw['createdAt'] : undefined,
    updatedAt: typeof raw['updatedAt'] === 'string' ? raw['updatedAt'] : undefined,
  };
}

function unwrapFormSelects(body: unknown): CostCenterFormSelects {
  const data = asRecord(unwrapData(body)) ?? asRecord(body);
  if (!data) throw new SettingsApiError('unexpected', 0);

  const typesRaw = firstArray(data, ['types', 'type']);
  const developmentsRaw = firstArray(data, ['developments', 'development']);

  return {
    types: typesRaw.map(normalizeTypeRef).filter((item): item is CostCenterTypeRef => !!item),
    developments: developmentsRaw
      .map(normalizeDevelopmentRef)
      .filter((item): item is CostCenterDevelopmentRef => !!item),
  };
}

function unwrapFilterSelects(body: unknown): CostCenterFilterSelects {
  const data = asRecord(unwrapData(body)) ?? asRecord(body);
  if (!data) throw new SettingsApiError('unexpected', 0);

  return {
    status: firstArray(data, ['status', 'statuses']).map(toFilterOption).filter(isFilterOption),
    types: firstArray(data, ['type', 'types']).map(toFilterOption).filter(isFilterOption),
  };
}

function normalizeTypeRef(raw: unknown): CostCenterTypeRef | null {
  const record = asRecord(raw);
  if (!record) return null;
  const uuid = stringField(record, ['uuid', 'id', 'value']);
  const name = stringField(record, ['name', 'label']);
  if (!uuid || !name) return null;
  return {
    uuid,
    name,
    projectRelated: booleanField(record, ['projectRelated', 'relatedToProject']) ?? false,
  };
}

function normalizeDevelopmentRef(raw: unknown): CostCenterDevelopmentRef | null {
  const record = asRecord(raw);
  if (!record) return null;
  const uuid = stringField(record, ['uuid', 'id', 'value']);
  const name = stringField(record, ['name', 'label']);
  if (!uuid || !name) return null;
  return { uuid, name };
}

function toFilterOption(raw: unknown): CostCenterFilterOption | null {
  if (typeof raw === 'string' && raw.trim()) {
    return { name: raw, value: raw };
  }
  const record = asRecord(raw);
  if (!record) return null;
  const name = stringField(record, ['name', 'label']) ?? '';
  const value = stringField(record, ['value', 'uuid', 'id', 'code']) ?? name;
  if (!name && !value) return null;
  return { name: name || value, value: value || name };
}

function isFilterOption(option: CostCenterFilterOption | null): option is CostCenterFilterOption {
  return option !== null;
}

function firstArray(record: Record<string, unknown>, keys: string[]): unknown[] {
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringField(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}

function booleanField(record: Record<string, unknown>, keys: string[]): boolean | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'boolean') return value;
  }
  return null;
}
